"use client";

import { useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { useCart } from "@/hooks/use-cart";
import { Button } from "@/components/ui/button";
import { Minus, Plus, Trash } from "lucide-react";

interface CartItemProps {
  item: {
    id: number;
    name: string;
    price: number;
    image_url: string;
    slug: string;
    variant_id?: string;
    variant_name?: string;
  };
  quantity: number;
}

export default function CartItem({ item, quantity }: CartItemProps) {
  const { addItem, removeItem, removeAll } = useCart();
  const [isUpdating, setIsUpdating] = useState(false);

  const formattedPrice = new Intl.NumberFormat('en-NG', {
    style: 'currency',
    currency: 'NGN',
    minimumFractionDigits: 0,
  }).format(item.price * quantity);

  const increaseQuantity = async () => {
    try {
      setIsUpdating(true);
      // Use the item directly since we've updated the interface to include variant_id
      addItem(item);
    } catch (error) {
      console.error('Error increasing quantity:', error);
    } finally {
      // Ensure we reset the updating state even if there's an error
      setTimeout(() => setIsUpdating(false), 300);
    }
  };

  const decreaseQuantity = async () => {
    try {
      setIsUpdating(true);
      // Use the proper variant_id from the item
      removeItem(item.id, item.variant_id);
    } catch (error) {
      console.error('Error decreasing quantity:', error);
    } finally {
      // Ensure we reset the updating state even if there's an error
      setTimeout(() => setIsUpdating(false), 300);
    }
  };

  return (
    <div className="p-4 hover:bg-muted/50 transition-colors">
      <div className="flex gap-4">
        <div className="relative h-20 w-20 flex-shrink-0 overflow-hidden rounded-md border bg-muted">
          <Image
            src={item.image_url}
            alt={item.name}
            fill
            className="object-cover"
          />
        </div>
        <div className="flex flex-1 flex-col">
          <div className="flex justify-between">
            <Link 
              href={`/product/${item.slug}`}
              className="text-sm font-medium hover:underline"
            >
              {item.name}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => removeItem(item.id, item.variant_id)}
              className="text-muted-foreground hover:text-destructive -mr-2"
            >
              <Trash className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-auto flex items-end justify-between">
            <div className="flex items-center space-x-2">
              <Button 
                variant="outline" 
                size="icon" 
                className="h-8 w-8"
                onClick={decreaseQuantity}
                disabled={quantity <= 1 || isUpdating}
              >
                <Minus className="h-3 w-3" />
              </Button>
              <span className="w-8 text-center text-sm">{quantity}</span>
              <Button 
                variant="outline" 
                size="icon"
                className="h-8 w-8"
                onClick={increaseQuantity}
                disabled={isUpdating}
              >
                <Plus className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-sm font-semibold">
              {formattedPrice}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}